<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { listPending, deletePending } from "../api.js";
import UploadCard from "./UploadCard.vue";

const props = defineProps({
  token: { type: String, required: true },
  selectedId: { type: String, default: null },
});
const emit = defineEmits(["open"]);

// "active" = the live inbox: processing (parse running) + pending (ready to
// review) + failed. approved/rejected/all are still selectable below.
const status = ref("active");
const entries = ref([]);
const loading = ref(true);
const error = ref(null);

// Poll so a just-arrived email — and its processing→pending transition once
// parsing finishes — shows up without a manual refresh. `silent` skips the
// spinner so the background polls don't make the ↻ flicker.
const POLL_MS = 4000;
let pollTimer = null;

async function load({ silent = false } = {}) {
  if (!silent) loading.value = true;
  error.value = null;
  try {
    const data = await listPending(props.token, status.value);
    entries.value = data.entries;
  } catch (e) {
    error.value = e.status === 401 ? "Token rejected (401)" : e.message;
    if (e.status === 401) {
      localStorage.removeItem("noviustec_token");
      stopPolling(); // don't keep hammering the API with a rejected token
    }
  } finally {
    loading.value = false;
  }
}

function startPolling() {
  stopPolling();
  pollTimer = setInterval(() => load({ silent: true }), POLL_MS);
}
function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

onMounted(() => {
  load();
  startPolling();
});
onBeforeUnmount(stopPolling);

// Only fully-parsed rows open the review panel — processing/failed aren't
// reviewable.
function onRowClick(e) {
  if (e.status === "pending") emit("open", e.id);
}

// Dismiss a failed entry: delete it server-side, then drop it from the list
// immediately (the next poll would do the same, but this feels instant).
async function dismiss(e) {
  try {
    await deletePending(props.token, e.id);
    entries.value = entries.value.filter((x) => x.id !== e.id);
  } catch (err) {
    error.value = `Couldn't remove entry: ${err.message}`;
  }
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toISOString().slice(0, 10);
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ""
    : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatTotal(amount, currency) {
  if (amount == null) return "—";
  return `${currency || ""} ${Number(amount).toFixed(2)}`.trim();
}

function confidenceClass(c) {
  if (c == null) return "";
  if (c >= 0.85) return "conf-high";
  if (c >= 0.5) return "conf-mid";
  return "conf-low";
}
</script>

<template>
  <section class="inbox">
    <header class="head">
      <h2>Inbox</h2>
      <div class="controls">
        <select v-model="status" @change="load()">
          <option value="active">Inbox</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
        <button @click="load()" :disabled="loading" class="refresh">
          {{ loading ? "…" : "↻" }}
        </button>
      </div>
    </header>

    <div class="upload-wrap">
      <UploadCard :token="token" @uploaded="load()" />
    </div>

    <p v-if="error" class="error">{{ error }}</p>

    <div v-if="!loading && entries.length === 0 && !error" class="empty">
      {{ status === "active" ? "Inbox is empty." : `No ${status} entries.` }}
    </div>

    <ul v-if="entries.length > 0" class="entries">
      <li
        v-for="e in entries"
        :key="e.id"
        @click="onRowClick(e)"
        :class="{
          resolved: e.status === 'approved' || e.status === 'rejected',
          processing: e.status === 'processing',
          failed: e.status === 'failed',
          selected: e.id === selectedId,
          clickable: e.status === 'pending',
        }"
      >
        <!-- Processing placeholder: email received, parse still running -->
        <template v-if="e.status === 'processing'">
          <div class="row1">
            <span class="vendor proc">
              <span class="spinner" aria-hidden="true"></span>
              Processing…
            </span>
            <span class="proc-actions">
              <span class="time">{{ formatTime(e.received_at) }}</span>
              <button
                class="dismiss"
                title="Remove this entry (cancels a stuck parse)"
                @click.stop="dismiss(e)"
              >✕</button>
            </span>
          </div>
          <div class="row2">
            <span v-if="e.reason" class="proc-sub">{{ e.reason }}</span>
          </div>
        </template>

        <!-- Parsed / failed / resolved -->
        <template v-else>
          <div class="row1">
            <span class="vendor">
              <span v-if="e.status === 'failed'" class="fail-tag">Couldn’t parse</span>
              <template v-else>{{ e.vendor || "(no vendor)" }}</template>
            </span>
            <button
              v-if="e.status === 'failed'"
              class="dismiss"
              title="Remove this entry"
              @click.stop="dismiss(e)"
            >✕</button>
            <span v-else class="total">{{ formatTotal(e.total, e.currency) }}</span>
          </div>
          <div class="row2">
            <span class="date">{{ formatDate(e.date) }}</span>
            <span class="cat" v-if="e.suggested_category">·</span>
            <span class="cat" v-if="e.suggested_category">
              {{ e.suggested_category }}
            </span>
            <span
              v-if="e.confidence != null"
              class="conf"
              :class="confidenceClass(e.confidence)"
            >
              {{ Math.round(e.confidence * 100) }}%
            </span>
            <span v-if="e.reason" class="reason">{{ e.reason }}</span>
          </div>
        </template>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.inbox {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 0.75rem;
  box-sizing: border-box;
}

/* Header + upload zone stay pinned; only .entries scrolls. */
.head,
.upload-wrap,
.error {
  flex: 0 0 auto;
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.upload-wrap {
  /* Cancels the bottom margin UploadCard ships with so the gap between
     it and the list stays tight in a sidebar context. */
  margin-bottom: 0;
}

h2 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.controls {
  display: flex;
  gap: 0.25rem;
  align-items: center;
}

.controls select {
  width: auto;
  font-size: 0.75rem;
  padding: 0.2rem 0.35rem;
}

.controls button.refresh {
  font-size: 0.95rem;
  padding: 0.15rem 0.45rem;
  line-height: 1;
}

.error {
  background: #fef2f2;
  color: var(--danger);
  padding: 0.5rem 0.6rem;
  border-radius: var(--radius);
  border: 1px solid #fca5a5;
  font-size: 0.8rem;
  margin: 0 0 0.5rem;
}

.empty {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--text-muted);
  padding: 1.5rem 0.5rem;
  font-size: 0.85rem;
}

.entries {
  /* Take all remaining vertical space, scroll independently. */
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.entries li {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 0.5rem 0.6rem;
  cursor: default;
  transition:
    background 0.1s ease,
    border-color 0.1s ease;
}

.entries li.clickable {
  cursor: pointer;
}

.entries li.clickable:hover {
  background: #f5f5f0;
  border-color: #d0d0c8;
}

.entries li.selected {
  background: #eff6ff;
  border-color: var(--accent);
  box-shadow: 0 0 0 1px var(--accent);
}

.entries li.resolved {
  opacity: 0.55;
}

/* Processing placeholder — email received, parse in flight. */
.entries li.processing {
  background: #fafaf5;
}

.vendor.proc {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 500;
  color: var(--text-muted);
}

.proc-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
}

.time {
  font-size: 0.7rem;
  color: var(--text-muted);
  white-space: nowrap;
}

.proc-sub {
  font-size: 0.7rem;
  font-style: italic;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spinner {
  width: 11px;
  height: 11px;
  border: 2px solid var(--border);
  border-top-color: var(--text-muted);
  border-radius: 50%;
  display: inline-block;
  animation: inbox-spin 0.7s linear infinite;
}

@keyframes inbox-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Failed — parse gave up (not a receipt / error). */
.entries li.failed {
  opacity: 0.75;
}

.fail-tag {
  color: var(--danger);
  font-weight: 600;
  font-size: 0.82rem;
}

.dismiss {
  flex: 0 0 auto;
  background: transparent;
  border: none;
  color: var(--text-muted);
  font-size: 0.85rem;
  line-height: 1;
  padding: 0.1rem 0.3rem;
  border-radius: 4px;
  cursor: pointer;
}

.dismiss:hover {
  background: #fef2f2;
  color: var(--danger);
}

.row1 {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.vendor {
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.total {
  font-family: var(--font-mono);
  font-weight: 500;
  font-size: 0.8rem;
  white-space: nowrap;
}

.row2 {
  display: flex;
  gap: 0.4rem;
  font-size: 0.7rem;
  color: var(--text-muted);
  margin-top: 0.2rem;
  flex-wrap: wrap;
  align-items: center;
}

.cat {
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
}

.conf {
  font-family: var(--font-mono);
}

.conf-high {
  color: var(--ok);
}

.conf-mid {
  color: var(--warn);
}

.conf-low {
  color: var(--danger);
}

.reason {
  color: var(--warn);
  font-style: italic;
  white-space: nowrap;
}
</style>
